import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Popover, Tooltip } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, FactCheck, MenuBook } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CustomDropDown from '../../CustomDropDown';
import CustomTextField from '../../CustomTextField';
import Loader from '../../Loader';
import { headingBlue, lightGrey, maroon, mediumGrey } from '../../../assets/colors';
import { tableOffsets } from '../../../utils/constants';
import LearningMaterialsDialog from './LearningMaterialsDialog';
import AttendanceDialog from './AttendanceDialog';
import moment from 'moment';
import HttpService from '../../../backend/requestApis';
import { fetchTrainingClasses, fetchTrainingProgramsList, fetchProgramBatches } from '../../../backend/backendApis';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

function TrainingClassesTable(props) {
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [materialsDialogStatus, setMaterialsDialogStatus] = useState(false);
    const [attendanceDialogStatus, setAttendanceDialogStatus] = useState(false);
    const [selectedClassId, setSelectedClassId] = useState(null);
    const [selectedClassDetails, setSelectedClassDetails] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [programsList, setProgramsList] = useState([]);
    const [batchesList, setBatchesList] = useState([]);

    useEffect(() => {
        let options = {
            url: fetchTrainingProgramsList,
            method: 'GET',
            headers: {
                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
            }
        };

        HttpService(options).then((resp) => {
            let records = resp?.data?.data || [];
            let formattedList = records.map(p => ({
                name: p.uniqueId ? `${p.uniqueId} - ${p.programName}` : p.programName,
                value: p._id || p.id || p.uniqueId
            }));
            setProgramsList(formattedList);
        }).catch(err => {
            console.log('Error fetching programs list: ', err);
        });
    }, []);

    useEffect(() => {
        let programId = undefined;
        if (props.advancedFilters?.program) {
            let selectedProg = programsList.find(p => p.name === props.advancedFilters.program);
            programId = selectedProg ? selectedProg.value : undefined;
        }

        let options = {
            url: fetchProgramBatches,
            method: 'GET',
            params: {
                programId: programId
            },
            headers: {
                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
            }
        };

        HttpService(options).then((resp) => {
            let records = resp?.data?.data || [];
            let formattedList = records.map(b => ({
                name: b.uniqueId ? `${b.uniqueId} - ${b.batchName || b.name}` : (b.batchName || b.name),
                value: b._id || b.id || b.uniqueId
            }));
            setBatchesList(formattedList);
        }).catch(err => {
            console.log('Error fetching batches list: ', err);
        });
    }, [props.advancedFilters?.program, programsList]);

    var outerBoxStyle = {
        width: '100%',  
        height: '70vh',
        marginTop: "0.4rem"
    };
    
    var paperStyle = {
        border: '1px solid #D9D9D9',
        padding: "0.2rem 0.1rem 0.1rem 0.1rem",
    };

    var headerBoxStyle = {
        display: 'flex',
        padding: "0.3rem",
        flexWrap: 'wrap'
    };
    
    var internalBoxStyle = {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '20%',
        flex: '0.25 1',
        flexDirection: "row",
        flexWrap: 'wrap'
    };

    var typographyStyle = {
        marginLeft: "0.5rem",
        padding: "0.1rem",
        fontSize: "1rem",
        fontWeight: "600"
    };

    var buttonStyle = {
        backgroundColor: headingBlue,
        color: 'white',
        marginLeft: "0.7rem",
        fontFamily: "Helvetica",
        fontWeight: '300',
        fontSize: '0.9rem',
        padding: "0.2rem 1rem 0.2rem 1rem",
        textTransform: 'capitalize'
    };

    var headerCellStyle = {
        padding: '0.2rem 0.5rem 0.1rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "1rem", 
        fontWeight: "400",
        textAlign: 'center',
    };

    const openMaterialsDialog = (classId) => {
        setSelectedClassId(classId);
        setMaterialsDialogStatus(true);
    };

    const openAttendanceDialog = (row) => {
        setSelectedClassId(row._id || row.classId);
        setSelectedClassDetails(row);
        setAttendanceDialogStatus(true);
    };

    const handleDownload = () => {
        setIsDownloading(true);
        let options = {
            url: fetchTrainingClasses,
            method: 'GET',
            params: {
                page: 1,
                offset: props?.data?.totalRecords || 10000,
                search: props.filterByValue,
                status: props.advancedFilters?.status !== 'All' ? props.advancedFilters?.status : undefined,
                program: props.advancedFilters?.program || undefined,
                batch: props.advancedFilters?.batch || undefined
            },
            headers: {
                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
            }
        };

        HttpService(options).then((resp) => {
            let records = resp?.data?.data?.data || [];
            
            const transformedData = records.map(row => {
                return {
                    'Class Date': row.classDate ? moment(row.classDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-',
                    'Program Name': row.programName || '-',
                    'Batch Name': row.batchName || '-',
                    'Attendance': row.attendanceStats ? `${row.attendanceStats.present} / ${row.attendanceStats.total}` : '-',
                    'Coordinator Name': row.coordinatorName || row.volunteerName || '-',
                    'Coordinator Contact': row.coordinatorNumber || row.volunteerCN || '-',
                    'Status': row.status || '-'
                };
            });

            const workBook = XLSX.utils.book_new();
            const workSheet = XLSX.utils.json_to_sheet(transformedData);
            XLSX.utils.book_append_sheet(workBook, workSheet, 'Classes');
            const excelBuffer = XLSX.write(workBook, {bookType: "xlsx", type: 'array'});
            const blob = new Blob([excelBuffer], {type: 'application/octet-stream'});
            saveAs(blob, 'Training_Classes.xlsx');
            setIsDownloading(false);
        }).catch(err => {
            console.log('Error downloading classes: ', err);
            setIsDownloading(false);
        });
    };

    return (
        <Box style={outerBoxStyle}>
            <Paper style={paperStyle}>
                <Box style={headerBoxStyle}>
                    <Box style={internalBoxStyle}>
                        <Typography style={typographyStyle}>Classes Table</Typography>
                    </Box>
                    <Box style={{ display:'flex', flex: "1 1", width: "80%", justifyContent: 'flex-end', padding: "0rem 0.5rem 0rem 0.5rem", flexWrap: 'wrap', gap: '0.5rem'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <CustomTextField placeHolder="Enter Text to Filter" value={props.filterByValue} setFunc={props.updateFilterBy}></CustomTextField>
                            <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)} size="small" style={{marginLeft: '0.5rem', padding: '2px', backgroundColor: headingBlue, color: 'white', borderRadius: '0.2rem'}}>
                                <FilterListIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Button variant='outlined' sx={buttonStyle} onClick={handleDownload} disabled={isDownloading}>{isDownloading ? 'Downloading...' : 'Download'}</Button>
                    </Box>
                </Box>
                
                <Box style={{display: 'flex', justifyContent: 'center', overflow: 'auto'}}>
                    <TableContainer component={Paper} style={{width: '98%', height: "55vh", border: '1px solid #D9D9D9', marginTop: "0.2rem", "&::-webkit-scrollbar": { display: 'none' }, scrollbarWidth: "none"}}>
                        <Table stickyHeader>
                            <TableHead style={{backgroundColor: mediumGrey}}>
                                <TableRow>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Class Date</TableCell>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Program Name</TableCell>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Batch Name</TableCell>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Attendance</TableCell>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Coordinator</TableCell>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Status</TableCell>
                                    <TableCell style={{...headerCellStyle, backgroundColor: lightGrey}}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody style={{ textAlign: 'center', overflowY: 'auto'}}>
                                {props.isLoading && <Loader />}
                                {props?.data?.data?.map((row, index) => {
                                    return(
                                        <TableRow key={index}>
                                            <TableCell style={{...headerCellStyle, color: headingBlue, fontSize: '0.9rem'}}>
                                                {row.classDate ? moment(row.classDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-'}
                                            </TableCell>
                                            <TableCell style={{...headerCellStyle, fontSize: '0.9rem'}}>
                                                {row.programName ? row.programName : '-'}
                                            </TableCell>
                                            <TableCell style={{...headerCellStyle, fontSize: '0.9rem'}}>
                                                {row.batchName ? row.batchName : '-'}
                                            </TableCell>
                                            <TableCell style={{...headerCellStyle, fontSize: '0.9rem'}}>
                                                {row.attendanceStats ? `${row.attendanceStats.present} / ${row.attendanceStats.total}` : '-'}
                                            </TableCell>
                                            <TableCell style={{...headerCellStyle, fontSize: '0.9rem'}}>
                                                {row.coordinatorName || row.volunteerName ? (
                                                    <Box>
                                                        <Box>{row.coordinatorName || row.volunteerName}</Box>
                                                        <Box style={{ fontSize: '0.75rem', color: 'gray' }}>
                                                            {row.coordinatorNumber || row.volunteerCN || '-'}
                                                        </Box>
                                                    </Box>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell style={{...headerCellStyle, fontSize: '0.9rem', textTransform: 'capitalize'}}>
                                                {row.status ? row.status : '-'}
                                            </TableCell>
                                            <TableCell style={{...headerCellStyle, fontSize: '0.9rem'}}>
                                                <Box style={{display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap'}}>
                                                    <Tooltip title="Attendance">
                                                        <IconButton 
                                                            size="small" 
                                                            style={{ color: headingBlue }}
                                                            onClick={() => openAttendanceDialog(row)}
                                                        >
                                                            <FactCheck fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Materials">
                                                        <IconButton 
                                                            size="small" 
                                                            style={{ color: headingBlue }}
                                                            onClick={() => openMaterialsDialog(row._id || row.classId)}
                                                        >
                                                            <MenuBook fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                
                {/* Pagination Box */}
                <Box style={{display: 'flex', justifyContent: 'center', padding: "0.4rem 0rem 0.1rem 0rem"}}>
                    <Box style={{width: "30%", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: "0rem 0.5rem 0rem 0.9rem"}}>
                        <Typography style={{color: maroon, fontSize: '0.9rem'}}>
                            {((props?.pageValue - 1) * props?.offsetValue) + 1} - {(props?.pageValue * props?.offsetValue) < (props?.data?.totalRecords) ? (props?.pageValue * props?.offsetValue) : props?.data?.totalRecords} of {props?.data?.totalRecords || 0}
                        </Typography>
                    </Box>
                    <Box style={{width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <IconButton size='small' onClick={() => props.pageValue > 1 && props.updatePage(props.pageValue - 1)} disabled={props.pageValue === 1}><KeyboardArrowLeft size='small' sx={{color: props.pageValue === 1 ? mediumGrey : headingBlue}} /></IconButton>
                        <Button variant='contained' style={{minWidth: '0rem', width: '1.5rem', height: '1.5rem', borderRadius: '50%', backgroundColor: headingBlue, color: 'white', padding: '0rem', alignItems: 'center'}}>{props.pageValue || 1}</Button>
                        <IconButton size='small' onClick={() => props.pageValue < props?.data?.totalPages && props.updatePage(props.pageValue + 1)} disabled={props.pageValue === props?.data?.totalPages || !props?.data?.totalPages}><KeyboardArrowRight size='small' sx={{color: (props.pageValue === props?.data?.totalPages || !props?.data?.totalPages) ? mediumGrey : headingBlue}} /></IconButton>
                    </Box>
                    <Box style={{width: '30%', display: 'flex', justifyContent: 'flex-end', padding: "0rem 0.6rem 0rem 0rem"}}>
                        <CustomDropDown key='table' value={props.offsetValue} width="5rem" setFunc={props.updateOffset} list={tableOffsets} regular={true} extraParamvalue={true}></CustomDropDown>
                    </Box>
                </Box>

                <Popover
                    open={Boolean(filterAnchorEl)}
                    anchorEl={filterAnchorEl}
                    onClose={() => setFilterAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: '1rem', width: '16rem' }}>
                        <Typography variant="subtitle2" style={{color: headingBlue, fontWeight: 'bold'}}>Advanced Filters</Typography>
                        <CustomDropDown
                            placeHolder="Status"
                            list={[{name: 'All'}, {name: 'scheduled'}, {name: 'ongoing'}, {name: 'completed'}, {name: 'cancelled'}]}
                            value={props.advancedFilters?.status || 'All'}
                            setFunc={(val) => props.updateAdvancedFilters({...props.advancedFilters, status: val})}
                        />
                        <CustomDropDown 
                            placeHolder="Select Program" 
                            list={programsList}
                            value={props.advancedFilters?.program || 'Select Program'} 
                            setFunc={(val) => props.updateAdvancedFilters({...props.advancedFilters, program: val === 'Select Program' ? '' : val, batch: ''})} 
                        />
                        <CustomDropDown 
                            placeHolder="Select Batch" 
                            list={batchesList}
                            value={props.advancedFilters?.batch || 'Select Batch'} 
                            setFunc={(val) => props.updateAdvancedFilters({...props.advancedFilters, batch: val === 'Select Batch' ? '' : val})} 
                        />
                        <Box style={{display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem'}}>
                            <Button 
                                onClick={() => {
                                    props.updateAdvancedFilters({status: 'All', program: '', batch: ''});
                                }}
                                size="small" 
                                style={{...buttonStyle, backgroundColor: 'white', color: headingBlue, border: `1px solid ${headingBlue}`, marginRight: '0.5rem'}}
                            >
                                Clear
                            </Button>
                            <Button onClick={() => setFilterAnchorEl(null)} size="small" style={buttonStyle}>Close</Button>
                        </Box>
                    </Box>
                </Popover>
                <LearningMaterialsDialog 
                    dialogStatus={materialsDialogStatus} 
                    updateFunc={setMaterialsDialogStatus} 
                    classId={selectedClassId} 
                />
                <AttendanceDialog 
                    dialogStatus={attendanceDialogStatus} 
                    updateFunc={setAttendanceDialogStatus} 
                    classId={selectedClassId} 
                    classDetails={selectedClassDetails}
                />
            </Paper>
        </Box>
    )
}

export default TrainingClassesTable;